/**
 * Supabase client (frontend).
 *
 * V2.1 (PR #23) — switched to @supabase/ssr's createBrowserClient with
 * cookies scoped to .annuityassociation.com so the session set by the
 * portal's sign-in (PR #22) is visible here on scheduler.annuityassociation.com.
 *
 * The public booking widget at "/" remains anonymous — it never reads
 * the session, just queries advisors / pools through RLS. The new
 * authenticated surfaces are:
 *   - /me               advisor's personal scheduler view (PR #23)
 *   - /admin/*          admin tooling — session banner only in PR #23,
 *                        full role-based gating in PR #24 (replaces ?key=)
 *
 * Server-side calls that need to bypass RLS still import the secret-key
 * client from api/_lib/supabase-server.ts. This file is purely browser.
 */

import { createBrowserClient, type CookieOptionsWithName } from '@supabase/ssr';
import type { Database } from './types';

const url = import.meta.env.VITE_SUPABASE_URL;
const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const COOKIE_DOMAIN = '.annuityassociation.com';

if (!url) {
  throw new Error(
    'VITE_SUPABASE_URL is not set. Add it to .env.local (dev) or Vercel env vars (prod).'
  );
}
if (!publishableKey) {
  throw new Error(
    'VITE_SUPABASE_PUBLISHABLE_KEY is not set. Add it to .env.local (dev) or Vercel env vars (prod).'
  );
}

function isProductionHost(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.endsWith('annuityassociation.com');
}

const sharedCookieOptions: CookieOptionsWithName = isProductionHost()
  ? {
      domain: COOKIE_DOMAIN,
      path: '/',
      sameSite: 'lax',
      secure: true,
      maxAge: 60 * 60 * 24 * 30,
    }
  : {
      path: '/',
      sameSite: 'lax',
    };

export const supabase = createBrowserClient<Database>(
  url,
  publishableKey,
  {
    cookieOptions: sharedCookieOptions,
  }
);
